import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, toast, Tooltip, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import {
  IconRefresh,
  IconPlayerPlay,
  IconCircleCheck,
  IconCircleX,
  IconPencil,
  IconTrash,
  IconInfoCircle,
} from "@tabler/icons-react";
import { getIconByDatastoreType } from "../DatastoreImage";
import { testDbtCloudConnection, triggerDbtCloudSync } from "@/helpers/apis";
import { DBT_CLOUD_QUERY_KEYS, DbtCloudConnectionResponse, DatastoreTypeEnum } from "../../types";

interface ConnectionCardProps {
  connection: DbtCloudConnectionResponse;
  syncOpAllowed: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const ConnectionCard = ({
  connection,
  syncOpAllowed,
  onEdit,
  onDelete,
}: ConnectionCardProps) => {
  const queryClient = useQueryClient();
  const [testState, setTestState] = useState<"idle" | "success" | "error">("idle");

  const { mutate: runTestConnection, isPending: isTestLoading } = useMutation({
    mutationFn: () => testDbtCloudConnection(connection.id),
    onSuccess: () => {
      setTestState("success");
      queryClient.invalidateQueries({ queryKey: [DBT_CLOUD_QUERY_KEYS.CONNECTION, connection.id] });
      setTimeout(() => setTestState("idle"), 5000);
    },
    onError: () => {
      setTestState("error");
      setTimeout(() => setTestState("idle"), 5000);
    },
  });

  const { mutate: runSyncConnection, isPending: isSyncing } = useMutation({
    mutationFn: () => triggerDbtCloudSync(connection.id),
    onSuccess: response => {
      toast({
        title: "Sync triggered",
        description: `Queued ${response?.environments_queued || 0} environments for sync.`,
      });
      queryClient.invalidateQueries({
        queryKey: [DBT_CLOUD_QUERY_KEYS.SYNC_HISTORY, connection.id],
      });
    },
    onError: (err: unknown) => {
      toast({
        variant: "destructive",
        title: "Sync failed",
        description: (err as Error)?.message || "Failed to trigger sync",
      });
    },
  });

  const handleTestConnection = (e: React.MouseEvent) => {
    e.stopPropagation();
    runTestConnection();
  };

  const handleSync = (e: React.MouseEvent) => {
    e.stopPropagation();
    runSyncConnection();
  };

  const getTestIcon = () => {
    if (isTestLoading) {
      return <IconRefresh size={20} className="al-animate-spin al-text-brand" />;
    }
    if (testState === "success") {
      return <IconCircleCheck size={20} className="al-text-green-500" />;
    }
    if (testState === "error") {
      return <IconCircleX size={20} className="al-text-destructive" />;
    }
    return <IconPlayerPlay size={20} className="al-text-brand" />;
  };

  const icon = getIconByDatastoreType({
    datastoreType: DatastoreTypeEnum.DBT_CLOUD,
    className: "al-rounded al-w-[60px] al-h-[60px] al-p-2",
  });

  return (
    <Card className="al-overflow-hidden al-rounded-lg al-border al-border-gray-200">
      <Stack direction="column" className="al-h-full">
        {/* Main content section */}
        <div className="al-p-4 al-relative">
          <Tooltip
            title={
              <Stack direction="column" gap="sm">
                <span>
                  If column details are not displaying for some models, verify that the catalog.json
                  file was included in your dbt artifacts upload.
                </span>
                <span>
                  Re-upload your connection artifacts with the catalog file to restore column-level
                  visibility.
                </span>
              </Stack>
            }
          >
            <span className="al-absolute al-top-3 al-right-3 al-cursor-help">
              <IconInfoCircle size={18} className="al-text-muted-foreground" />
            </span>
          </Tooltip>
          <Stack direction="row" align="center" gap="sm">
            {icon}
            <Stack direction="column" gap="xxs">
              <Typography weight="regular" size="regular">
                {connection.name}
              </Typography>
              <Typography size="sm" className="al-text-brand">
                dbt Cloud
              </Typography>
            </Stack>
          </Stack>
          <Typography size="sm" className="al-text-foreground al-mt-2">
            Account ID: {connection.account_id}
          </Typography>
        </div>

        {/* Footer section with border-top */}
        <div className="al-border-t al-border-gray-200 al-bg-white al-px-4 al-py-3 al-mt-auto">
          <Stack direction="row" justify="between" align="center">
            <Stack direction="row" align="center" gap="xs">
              {syncOpAllowed ? (
                <Button
                  variant="ghost"
                  className="al-gap-1.5 al-bg-white al-rounded-md al-px-3 al-py-2"
                  onClick={handleSync}
                  aria-label="Sync now"
                >
                  <IconRefresh
                    size={20}
                    className={`al-text-brand ${isSyncing ? "al-animate-spin" : ""}`}
                  />
                  <Typography size="sm" weight="regular" className="al-text-brand">
                    {isSyncing ? "Syncing..." : "Sync Now"}
                  </Typography>
                </Button>
              ) : (
                <Tooltip title="Insufficient permissions for performing sync">
                  <Button
                    variant="ghost"
                    className="al-gap-1.5 al-bg-white al-rounded-md al-px-3 al-py-2"
                    disabled
                    aria-label="Sync now (disabled)"
                  >
                    <IconRefresh size={20} className="al-text-gray-400" />
                    <Typography size="sm" weight="regular" className="al-text-gray-400">
                      Sync Now
                    </Typography>
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Test Connection">
                <Button
                  variant="ghost"
                  className="al-gap-1.5 al-bg-[#f1f7ff] al-rounded-md al-px-3 al-py-2"
                  onClick={handleTestConnection}
                  aria-label="Test connection"
                >
                  {getTestIcon()}
                  <Typography size="sm" weight="regular" className="al-text-brand">
                    Test
                  </Typography>
                </Button>
              </Tooltip>
            </Stack>
            <Stack direction="row" align="center" gap="xs">
              {testState === "success" && (
                <Typography size="sm" className="al-text-green-500">
                  Success
                </Typography>
              )}
              {testState === "error" && (
                <Typography size="sm" className="al-text-destructive">
                  Failed, Try Again
                </Typography>
              )}
              {syncOpAllowed && onEdit && (
                <Tooltip title="Edit Connection">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      onEdit();
                    }}
                    aria-label="Edit connection"
                  >
                    <IconPencil size={18} className="al-text-gray-500" />
                  </Button>
                </Tooltip>
              )}
              {syncOpAllowed && onDelete && (
                <Tooltip title="Delete Connection">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      onDelete();
                    }}
                    aria-label="Delete connection"
                  >
                    <IconTrash size={18} className="al-text-destructive" />
                  </Button>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </div>
      </Stack>
    </Card>
  );
};
